import { useCallback, useEffect, useState } from "react";
import { GiWeight } from "react-icons/gi";
import { LuCopyPlus } from "react-icons/lu";
import { Link, useNavigate } from "react-router-dom";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import ModalCadastroAnimal from "../../components/modais/ModalCadastroAnimal";
import { repositorioAnimal, repositorioPesagem } from "../../sync/repositories/pecuaria";

const AJUDA = {
  titulo: "Pesagem de Animais",
  descricao:
    "Registre aqui o peso de um animal em uma data específica. A tela mostra automaticamente o lote atual do animal e o peso registrado na entrada desse lote, calculando o ganho de peso obtido até a pesagem.",
  secoes: [
    {
      titulo: "Animal",
      campos: [
        { nome: "Animal", obrigatorio: true, explicacao: "Animal que está sendo pesado. Se o animal ainda não existe, use o botão '+' ao lado do campo para cadastrá-lo." },
      ],
    },
    {
      titulo: "Pesos",
      campos: [
        { nome: "Peso Atual", obrigatorio: true, explicacao: "Peso do animal no momento desta pesagem." },
        { nome: "Peso de Entrada", obrigatorio: false, explicacao: "Peso registrado quando o animal entrou no lote atual. É preenchido automaticamente e não pode ser editado." },
      ],
    },
    {
      titulo: "Data",
      campos: [
        { nome: "Data da Pesagem", obrigatorio: true, explicacao: "Data em que a pesagem foi realizada." },
      ],
    },
  ],
  dicas: [
    "Pesagens funcionam normalmente offline — o registro sincroniza depois, quando a internet voltar.",
    "O Lote Atual e o Peso de Entrada são preenchidos automaticamente a partir do lote em que o animal está e não podem ser editados diretamente nesta tela.",
  ],
};

export default function GestaoPesagens() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [pesagens, setPesagens] = useState([]);
  const [pesagensFiltradas, setPesagensFiltradas] = useState([]);
  const [editando, setEditando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  
  // Dados para os selects
  const [animais, setAnimais] = useState([]);
  const [mostrarModalAnimal, setMostrarModalAnimal] = useState(false);
  
  const [filtros, setFiltros] = useState({
    busca: "",
    data_inicio: "",
    data_fim: ""
  });
  
  const [formData, setFormData] = useState({
    animal: "",
    lote: "",
    peso_entrada: "",
    peso: "",
    data_pesagem: "",
  });

  // Carregar animais
  const carregarAnimais = useCallback(async () => {
    try {
      const response = await repositorioAnimal.listar();
      const dados = response.data.results || response.data;
      // Garantir que seja um array
      const animaisArray = Array.isArray(dados) ? dados : [];
      setAnimais(animaisArray);
    } catch (error) {
      console.error("Erro ao carregar animais:", error);
      setAnimais([]);
    }
  }, []);

  // Carregar pesagens
  const carregarPesagens = useCallback(async () => {
    try {
      setCarregandoLista(true);
      const response = await repositorioPesagem.listar();
      const dados = response.data.results || response.data;
      setPesagens(Array.isArray(dados) ? dados : []);
      setPesagensFiltradas(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar pesagens:", error);
      alert("Erro ao carregar lista de pesagens");
    } finally {
      setCarregandoLista(false);
    }
  }, []);

  useEffect(() => {
    carregarPesagens();
    carregarAnimais();
  }, [carregarPesagens, carregarAnimais]);

  // Buscar dados do animal selecionado
  useEffect(() => {
    if (!formData.animal) {
      setFormData(prev => ({
        ...prev,
        lote: "",
        peso_entrada: "",
      }));
      return;
    }
    
    const animalSelecionado = animais.find(a => a.id === Number(formData.animal));
    
    if (animalSelecionado) {
      setFormData(prev => ({
        ...prev,
        lote: animalSelecionado.lote_atual?.nome || "Sem lote",
        peso_entrada: animalSelecionado.lote_atual?.peso_entrada || "",
      }));
    }
  }, [formData.animal, animais]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validarFormulario = () => {
    if (!formData.animal) {
      alert("Selecione um animal.");
      return false;
    }
    if (!formData.peso || Number(formData.peso) <= 0) {
      alert("Informe um peso válido.");
      return false;
    }
    if (!formData.data_pesagem) {
      alert("Informe a data da pesagem.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validarFormulario()) return;
    
    setLoading(true);

    const payload = {
      animal: formData.animal,
      peso: Number(formData.peso),
      data_pesagem: formData.data_pesagem,
    };

    try {
      if (editando) {
        await repositorioPesagem.atualizar(editandoId, payload);
        alert("Pesagem atualizada com sucesso!");
        handleCancel();
        carregarPesagens();
        carregarAnimais();
      } else {
        await repositorioPesagem.criar(payload);
        alert("Pesagem registrada com sucesso!");
        handleCancel();
        carregarPesagens();
        carregarAnimais();
      }
    } catch (error) {
      console.error("Erro:", error);
      const data = error.response?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const erros = Object.values(data).flat();
        alert(erros.join("\n"));
      } else if (Array.isArray(data)) {
        alert(data.join("\n"));
      } else {
        alert("Erro ao salvar. Verifique os dados e tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (pesagem) => {
    setFormData({
      animal: pesagem.animal?.id || pesagem.animal || "",
      lote: pesagem.lote_nome || "",
      peso_entrada: pesagem.peso_entrada || "",
      peso: pesagem.peso || "",
      data_pesagem: pesagem.data_pesagem || "",
    });
    setEditando(true);
    setEditandoId(pesagem.id);
    setMostrarFormulario(true);
    
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleNovaPesagem = () => {
    handleCancel();
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setFormData({
      animal: "",
      lote: "",
      peso_entrada: "",
      peso: "",
      data_pesagem: "",
    });
    setEditando(false);
    setEditandoId(null);
    setMostrarFormulario(false);
  };

  const handleExcluir = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir esta pesagem? Esta ação não pode ser desfeita.")) {
      return;
    }
    
    try {
      await repositorioPesagem.excluir(id);
      alert("Pesagem excluída com sucesso!");
      carregarPesagens();
      
      if (editandoId === id) {
        handleCancel();
      }
    } catch (error) {
      console.error("Erro ao excluir:", error);
      alert("Erro ao excluir pesagem.");
    }
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    let filtrados = Array.isArray(pesagens) ? [...pesagens] : [];
    
    if (filtros.busca) {
      const buscaLower = filtros.busca.toLowerCase();
      filtrados = filtrados.filter(p => 
        p.animal_sisbov?.toLowerCase().includes(buscaLower) ||
        p.animal_nome?.toLowerCase().includes(buscaLower) ||
        p.animal_brinco?.toLowerCase().includes(buscaLower)
      );
    }
    
    if (filtros.data_inicio) {
      filtrados = filtrados.filter(p => p.data_pesagem >= filtros.data_inicio);
    }
    
    if (filtros.data_fim) {
      filtrados = filtrados.filter(p => p.data_pesagem <= filtros.data_fim);
    }
    
    setPesagensFiltradas(filtrados);
  };

  const limparFiltros = () => {
    setFiltros({ busca: "", data_inicio: "", data_fim: "" });
    setPesagensFiltradas(Array.isArray(pesagens) ? pesagens : []);
  };

  const formatarData = (data) => {
    if (!data) return "—";
    return new Date(data).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  };

  const formatarPeso = (peso) => {
    if (!peso && peso !== 0) return "—";
    return `${Number(peso).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg`;
  };

  const getAnimalDisplay = (animal) => {
    const identificacao = animal.numero_identificacao || animal.sisbov || `ID: ${animal.id}`;
    const nome = animal.nome || "Sem nome";
    const especie = animal.especie_nome || "";
    const raca = animal.raca_nome || "";
    
    return `${identificacao} - ${nome} | ${especie} ${raca}`;
  };

  const calcularGanho = (pesoEntrada, pesoSaida) => {
    if (!pesoEntrada || !pesoSaida) return null;
    return pesoSaida - pesoEntrada;
  };

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">Gestão de Pesagens <GiWeight size={38} /></h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button
              onClick={handleNovaPesagem}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nova Pesagem
            </button>
          )}
        </div>
        
        {/* Formulário de Cadastro/Edição */}
        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {editando ? "Editar Pesagem" : "Registrar Nova Pesagem"}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700"
                title="Fechar formulário"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                {/* Linha 1: Animal e Lote */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Animal *</label>
                    <div className="flex items-center gap-2">
                      <select
                        name="animal"
                        value={formData.animal}
                        onChange={handleChange}
                        className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        required
                      >
                        <option value="">Selecione um animal...</option>
                        {Array.isArray(animais) && animais.length > 0 ? (
                          animais.map(a => (
                            <option key={a.id} value={a.id}>
                              {getAnimalDisplay(a)}
                            </option>
                          ))
                        ) : (
                          <option value="" disabled>Nenhum animal encontrado</option>
                        )}
                      </select>
                      <button
                        type="button"
                        onClick={() => setMostrarModalAnimal(true)}
                        title="Cadastrar novo animal"
                        className="p-2 hover:bg-gray-200 rounded"
                      >
                        <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Lote Atual</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        name="lote"
                        value={formData.lote || "Sem lote"}
                        readOnly
                        className="flex-1 border border-gray-300 p-2 rounded bg-gray-100 text-gray-600"
                      />
                      <Link
                        to="/pecuaria/lotes"
                        title="Adicionar novo lote"
                        className="p-2 hover:bg-gray-200 rounded"
                      >
                        <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Linha 2: Peso Entrada e Peso Atual */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Peso de Entrada</label>
                    <div className="flex items-center border border-gray-300 rounded bg-gray-100">
                      <input
                        type="text"
                        name="peso_entrada"
                        value={formatarPeso(formData.peso_entrada)}
                        readOnly
                        className="flex-1 p-2 rounded-l bg-gray-100 text-gray-600"
                      />
                      <span className="px-3 text-gray-700 bg-gray-100 rounded-r">kg</span>
                    </div>
                    <span className="text-xs text-gray-500 mt-1">
                      Peso registrado na entrada do lote
                    </span>
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Peso Atual *</label>
                    <div className="flex items-center border border-green-500 rounded">
                      <input
                        type="number"
                        step="0.01"
                        name="peso"
                        value={formData.peso}
                        onChange={handleChange}
                        className="flex-1 p-2 rounded-l focus:ring-2 focus:ring-green-300"
                        placeholder="0,00"
                        required
                      />
                      <span className="px-3 text-gray-700 bg-gray-100 rounded-r">kg</span>
                    </div>
                    <span className="text-xs text-gray-500 mt-1">
                      Peso atual do animal na pesagem
                    </span>
                  </div>
                </div>

                {/* Linha 3: Data da Pesagem e Ganho */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Data da Pesagem *</label>
                    <input
                      type="date"
                      name="data_pesagem"
                      value={formData.data_pesagem}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300 w-64"
                      required
                    />
                  </div>

                  {formData.peso_entrada && formData.peso && (
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Ganho de Peso</label>
                      <div className={`flex items-center rounded p-2 ${calcularGanho(parseFloat(formData.peso_entrada), parseFloat(formData.peso)) > 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                        <span className={`text-lg font-semibold ${calcularGanho(parseFloat(formData.peso_entrada), parseFloat(formData.peso)) > 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {calcularGanho(parseFloat(formData.peso_entrada), parseFloat(formData.peso)) > 0 ? '+' : ''}
                          {formatarPeso(calcularGanho(parseFloat(formData.peso_entrada), parseFloat(formData.peso)))}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Botões do formulário */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:bg-blue-300"
                  >
                    {loading ? "Salvando..." : (editando ? "Atualizar" : "Registrar")}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
        
        {/* Seção de Filtros - Sempre visível quando não está editando */}
        {!mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filtros de Busca
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">Buscar por Animal</label>
                <input
                  type="text"
                  placeholder="SISBOV, brinco ou nome do animal..."
                  value={filtros.busca}
                  onChange={(e) => setFiltros({...filtros, busca: e.target.value})}
                  className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                />
              </div>
              
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">Data Início</label>
                <input
                  type="date"
                  value={filtros.data_inicio}
                  onChange={(e) => setFiltros({...filtros, data_inicio: e.target.value})}
                  className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                />
              </div>
              
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">Data Fim</label>
                <input
                  type="date"
                  value={filtros.data_fim}
                  onChange={(e) => setFiltros({...filtros, data_fim: e.target.value})}
                  className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-4">
              <button
                onClick={aplicarFiltros}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
              >
                Aplicar Filtros
              </button>
              <button
                onClick={limparFiltros}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
              >
                Limpar Filtros
              </button>
            </div>
          </div>
        )}

        {/* Tabela de Pesagens - Sempre visível quando não está editando */}
        {!mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <p className="text-gray-600">
                {Array.isArray(pesagensFiltradas) ? pesagensFiltradas.length : 0} pesagem(s) registrada(s)
              </p>
            </div>
            
            {carregandoLista ? (
              <div className="p-8 text-center text-gray-500">Carregando...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Código</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Data</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Animal</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Lote</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Peso Entrada</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Peso Atual</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Ganho</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(pesagensFiltradas) && pesagensFiltradas.map((pesagem) => {
                      const ganho = calcularGanho(pesagem.peso_entrada, pesagem.peso);
                      return (
                        <tr key={pesagem.id} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                          <td className="p-3 text-sm font-mono">{pesagem.id}</td>
                          <td className="p-3 text-sm">{formatarData(pesagem.data_pesagem)}</td>
                          <td className="p-3 text-sm font-medium">
                            <span className="flex items-center gap-1">
                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              {pesagem.animal_sisbov || pesagem.animal_brinco || `ID: ${pesagem.animal}`}
                            </span>
                            <div className="text-xs text-gray-500">{pesagem.animal_nome || ""}</div>
                          </td>
                          <td className="p-3 text-sm">{pesagem.lote_nome || "—"}</td>
                          <td className="p-3 text-sm">{formatarPeso(pesagem.peso_entrada)}</td>
                          <td className="p-3 text-sm font-medium">{formatarPeso(pesagem.peso)}</td>
                          <td className="p-3 text-sm">
                            {ganho !== null && (
                              <span className={`font-medium ${ganho > 0 ? 'text-green-600' : ganho < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                                {ganho > 0 ? '+' : ''}{formatarPeso(ganho)}
                              </span>
                            )}
                           </td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditar(pesagem)}
                                className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Editar"
                              >
                                Editar
                              </button>
                              <button
                                onClick={() => handleExcluir(pesagem.id)}
                                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Excluir"
                              >
                                Excluir
                              </button>
                            </div>
                           </td>
                         </tr>
                      );
                    })}
                    
                    {(!Array.isArray(pesagensFiltradas) || pesagensFiltradas.length === 0) && (
                      <tr>
                        <td colSpan="8" className="p-8 text-center text-gray-500">
                          {filtros.busca || filtros.data_inicio || filtros.data_fim
                            ? "Nenhuma pesagem encontrada com os filtros selecionados."
                            : "Nenhuma pesagem registrada."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      <ModalCadastroAnimal
        isOpen={mostrarModalAnimal}
        onClose={() => setMostrarModalAnimal(false)}
        onSuccess={async (novo) => {
          await carregarAnimais();
          setFormData((prev) => ({ ...prev, animal: novo.id }));
        }}
      />
    </Layout>
  );
}